import { useState } from 'react';
import { IMerchant, FormField, Dependency, DependentField } from '~/Models/DTO/Merchant.models';
import config from './merchantConfig.json';
import { useTranslation } from 'react-i18next';
import { useValidateIBAN } from '~/requests/providerService/useRequestMerchants';
import { useUser } from '~/Hooks';
const getFieldByName = (name: string) => config?.fields?.find((field) => field.name === name);

const validateDependency = (
  values: IMerchant,
  dependency = [] as Dependency | Dependency[],
): boolean => {
  let isInvalid = false;
  if (Array.isArray(dependency[0])) {
    for (const dep of dependency as Dependency[]) {
      isInvalid = validateDependency(values, dep);
      if (!isInvalid) {
        break;
      }
    }
  } else {
    const dep = dependency as Dependency;
    dep.forEach(({ field, value }: DependentField) => {
      const validValue = values?.[field as keyof IMerchant]?.toString();
      if (validValue && validValue?.length && !value.includes(validValue)) {
        isInvalid = true;
      }
    });
  }
  return isInvalid;
};

export const checkDependencyByName = (name: string, values: IMerchant) => {
  const currentField = getFieldByName(name);
  const dependency = currentField?.dependency as any;
  if (!dependency) return false;
  const isInvalid = validateDependency(values, dependency);
  return isInvalid;
};

export const mapFieldsIsRequired = (values: IMerchant, fields: FormField[]) =>
  fields.map((field) => {
    const { required } = field;
    let isRequired;
    if (Array.isArray(required)) {
      for (const dep of required as Dependency[]) {
        isRequired = !validateDependency(values, dep);
        if (isRequired) {
          break;
        }
      }
    } else {
      isRequired = required;
    }
    return { ...field, required: isRequired };
  });

const validateRequired = (val: any, t: Function) => {
  const errors = {} as any;
  mapFieldsIsRequired(val, config?.fields)
    ?.filter(({ name }: FormField) => !checkDependencyByName(name, val))
    .forEach(({ name, label, required }: FormField) => {
      let isRequired = false;
      if (required) {
        isRequired = !val?.[name];
        if (isRequired) {
          errors[name || ''] = `${t(label)} is required`;
        }
        if (!val?.[name]?.length) {
          errors[name || ''] = `${t(label)} is required`;
        }
      }
    });
  return errors;
};

function isNumeric(value: any) {
  return !isNaN(parseFloat(value)) && isFinite(value);
}

function isAlphanumeric(value: any): boolean {
  return isNumeric(value) || (typeof value === 'string' && /^[a-zA-Z0-9]+$/.test(value));
}

function hasValidLength(value: string, min: number, max: number) {
  const valueLength = value.length;
  return valueLength >= min && valueLength <= max;
}

export const useMerchantValidations = (merchant: IMerchant, isCreate = false) => {
  const { t } = useTranslation();
  const { validateIBAN } = useValidateIBAN();

  const { DANGEROUSLY_isSuperAdmin: isAdmin } = useUser();
  const DANGEROUSLY_isSuperAdmin = isAdmin || isCreate;
  const [values, setValues] = useState(merchant);
  const [errors, setErrors] = useState({ initial: '' } as any);

  const validate = (val: any) => {
    let errors = null;
    let newValue = { ...val };

    //required validation
    const requiredErrors = validateRequired(val, t);
    errors = { ...requiredErrors };

    if (!val.consent) {
      errors.consent = t('merchantForm.consent.validation');
    }
    setValues(newValue);
    return errors;
  };

  const asyncValidate = async (val: any) => {
    let errors = validate(val);

    //check Postal Code
    if (!errors.postcode && !['pt'].includes(val.country) && !isAlphanumeric(val.postcode)) {
      errors.postcode = t('merchantForm.postcode.invalid');
    }

    //check Tax ID
    if (!errors.taxId && !hasValidLength(val.taxId, 9, 13)) {
      errors.taxId = t('merchantForm.taxId.invalid');
    }

    // IBAN validation TBD
    const { iban } = val;
    let ibanError = {};
    if (DANGEROUSLY_isSuperAdmin && !!iban) {
      const { isValid, error } = await validateIBAN(iban);
      ibanError = !isValid ? { iban: error } : {};
    }
    errors = { ...errors, ...ibanError };

    setErrors(errors);
    return errors;
  };

  return {
    validate,
    asyncValidate,
    values,
    errors,
  };
};
